// toolRouter.js - Intelligent Tool Selection & Routing
// PRODUCTION VERSION - Brave Search enabled for deployment

import alphaVantageService from './alphaVantageService';
import braveSearchService from './braveSearchService';

// Crypto symbols detection
const CRYPTO_SYMBOLS = [
  'BTC', 'ETH', 'XRP', 'ADA', 'SOL', 'DOGE', 'MATIC', 'DOT', 
  'LINK', 'UNI', 'AVAX', 'ATOM', 'LTC', 'BCH', 'XLM', 'ALGO',
  'VET', 'ICP', 'FIL', 'SAND', 'MANA', 'SHIB', 'CRO', 'NEAR'
];

// Stock symbols (common ones)
const STOCK_SYMBOLS = [
  'AAPL', 'TSLA', 'MSFT', 'GOOGL', 'AMZN', 'META', 'NVDA', 
  'AMD', 'NFLX', 'DIS', 'BABA', 'TSM', 'V', 'JPM', 'WMT'
];

/**
 * Detect if query needs crypto/stock price lookup
 */
const detectPriceQuery = (query) => {
  const upperQuery = query.toUpperCase();
  
  // Check for crypto symbols
  for (const symbol of CRYPTO_SYMBOLS) {
    if (upperQuery.includes(symbol)) {
      if (
        upperQuery.includes('PRICE') ||
        upperQuery.includes('COST') ||
        upperQuery.includes('WORTH') ||
        upperQuery.includes('VALUE') ||
        upperQuery.includes('HOW MUCH') ||
        upperQuery.includes('CURRENT') ||
        upperQuery.includes('NOW') ||
        /\$/.test(upperQuery)
      ) {
        return { type: 'crypto', symbol };
      }
    }
  }
  
  // Check for stock symbols
  for (const symbol of STOCK_SYMBOLS) {
    if (upperQuery.includes(symbol)) {
      if (
        upperQuery.includes('PRICE') ||
        upperQuery.includes('STOCK') ||
        upperQuery.includes('WORTH')
      ) {
        return { type: 'stock', symbol };
      }
    }
  }
  
  return null;
};

/**
 * Detect if query needs web search
 * ENABLED for production deployment
 */
const detectSearchQuery = (query) => {
  const lowerQuery = query.toLowerCase();
  
  // Search keywords
  const searchKeywords = [
    'search', 'find', 'look up', 'lookup', 'google',
    'latest', 'recent', 'news', 'today', 'yesterday',
    'what happened', 'update', 'current', 'now',
    'tell me about', 'information about'
  ];
  
  // Check if any search keyword present
  const hasSearchKeyword = searchKeywords.some(keyword => 
    lowerQuery.includes(keyword)
  );
  
  // Also detect questions that might need real-time info
  const needsRealTimeInfo = (
    /when (did|was|is)/.test(lowerQuery) ||
    /what (is|are|was) (the )?(latest|current|recent)/.test(lowerQuery) ||
    /how (is|are|was)/.test(lowerQuery)
  );
  
  return hasSearchKeyword || needsRealTimeInfo;
};

/**
 * Route query to appropriate tool and get results
 * PRODUCTION VERSION - All tools enabled
 */
export const routeQuery = async (query) => {
  const results = [];
  
  // Check for price query
  const priceDetection = detectPriceQuery(query);
  if (priceDetection) {
    try {
      const priceData = await alphaVantageService.getPrice(priceDetection.symbol);
      
      if (priceData.success) {
        results.push({
          type: 'price',
          data: priceData,
          context: `Current ${priceData.symbol} price: $${priceData.price.toFixed(2)}${
            priceData.change 
              ? ` (${priceData.change >= 0 ? '+' : ''}${priceData.change.toFixed(2)}, ${priceData.changePercent})`
              : ''
          }`
        });
      }
    } catch (error) {
      console.error('Price lookup failed:', error);
    }
  }
  
  // Check for search query - ENABLED for production
  if (detectSearchQuery(query)) {
    try {
      const searchData = await braveSearchService.search(query, { count: 5 });
      
      if (searchData.success && searchData.results.length > 0) {
        const searchContext = searchData.results
          .slice(0, 3)
          .map((r, i) => `${i + 1}. ${r.title}\n   ${r.description}`)
          .join('\n\n');
        
        results.push({
          type: 'search',
          data: searchData,
          context: `Web search results for "${query}":\n\n${searchContext}`
        });
      }
    } catch (error) {
      console.error('Web search failed:', error);
      // Don't fail the whole request if search fails
    }
  }
  
  return results;
};

/**
 * Build enhanced context with tool results
 */
export const buildEnhancedContext = (userQuery, toolResults) => {
  if (toolResults.length === 0) {
    return userQuery;
  }
  
  const contextParts = toolResults.map(r => r.context);
  const enhancedContext = `${contextParts.join('\n\n---\n\n')}

User question: ${userQuery}

Please answer based on the information provided above.`;
  
  return enhancedContext;
};

export default {
  routeQuery,
  buildEnhancedContext,
  detectPriceQuery,
  detectSearchQuery
};
